/**
 * #605 — the Papan host's side of an app's `papan.files.pickAndRead()` and
 * `pickAndSave()`, for the app running in an HtmlViewer.
 *
 * The app asks over postMessage; the host begins a request bound to the app's
 * OWN serve session, shows Papan's picker page (never inside the app's
 * document), and relays the answer: URLs under that session, never ids. Bytes
 * never cross postMessage — the app fetches (read) or uploads (save) itself.
 *
 * Two ways to show the picker, by who this host can ask as:
 *   - in Papan (the person's own session): a same-origin modal over the app
 *     (HandoffDialog). A pick's dialog closes when the person answers; a
 *     save's stays after the answer — the page follows the upload to its end
 *     so the PERSON learns where it went, and says `closed` when they are done.
 *   - in share-view (the API client acts as the share, not the person): a
 *     popup, begun through the app's share session (handoff-popup.ts) — the
 *     person signs in there if they must, and answers as themselves.
 *
 * One ask at a time, and only from an app the person can see. The picker
 * opens at once for an ask made with a click (`clickedNow`) — and only then:
 * an app asking by itself (on load, from a timer) is refused, as a browser
 * refuses its own file dialog without one. A person who closes the picker
 * can open it again at once with their next click. An ask whose app leaves
 * the screen (the viewer reloaded it into its other frame) ends.
 *
 * A DROP (`papan.files.readDrop`) shows no picker: the person already chose by
 * dragging from Papan onto the app. The app hands over the drag's token; the
 * host redeems it — as the person in Papan, through the share session in
 * share-view — for the same answer a pick gives.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react'
import { currentSessionSignal, getActiveTenantId, sessionEndedSince, sessionFetch } from '@papan/api-client'
import { useToastOptional } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { handoffApi, type HandoffDropResult } from '@papan/files/client/handoff'
import { HANDOFF_LIMITS, saveAnswerGrants, type HandoffOutcome } from '@papan/types'
import { HANDOFF_DIALOG_ATTR, HandoffDialog, type HandoffDialogOutcome } from './HandoffDialog.js'
import { askByPopup, popupClosed } from './handoff-popup.js'
import type { MessageHandlerMap } from './iframe-message-gate.js'
import { papanInput } from './papan-input.js'
import { replyTargetOf } from './app-origin-frame.js'

/**
 * The app's serve session, from a session-mode baseUrl (`<origin>/s/<sessionId>`)
 * — null in inline mode (a `srcDoc` preview has none). Handoff grants are bound
 * to this session, and their URLs live under it (`/s/<sessionId>/_papan/handoff/…`).
 */
export function serveSessionOf(baseUrl: string | null | undefined): { origin: string; sessionId: string } | null {
  if (!baseUrl) return null
  try {
    const u = new URL(baseUrl)
    const m = /^\/s\/([^/]+)\/?$/.exec(u.pathname)
    return m ? { origin: u.origin, sessionId: decodeURIComponent(m[1]) } : null
  } catch {
    return null
  }
}

type Kind = 'read' | 'save'

/**
 * Page-wide, across every viewer (each has its own relay): the ONE picker —
 * modal or popup — that may be up. Another viewer's app must not open its
 * picker over this one's.
 */
const page: { picker: Owner | null } = { picker: null }

/** Tests only: the page's picker state lives for the page's life. */
export function __resetHandoffPage(): void {
  page.picker = null
}

interface Active {
  /** Null while the request is being registered: the dialog shows, loading. */
  pid: string | null
  tenantId: string
  kind: Kind
}

/**
 * The ask holding the slot — one at a time. Everything that can end an ask
 * (the person, the app, the viewer) goes through its owner, so an ask that
 * was replaced can never end the one that replaced it.
 */
interface Owner {
  id: number
  /** The app's iframe (null only for an app not in this page's frames). */
  frame: HTMLIFrameElement | null
  /** The app's request id — until the person answered a save (a late cancel
   *  from the app is then no cancel of the picker). */
  requestId: string | null
  /** The app gave it up (its AbortSignal), possibly before anything showed. */
  appGaveUp: boolean
  /** A reload swap took the app's frame off screen: whatever phase the ask is
   *  in, nothing more is shown for it (its answer would reach a page nobody
   *  sees). */
  offScreen: boolean
  /** The person answered this save in the modal: from that word it is theirs
   *  — a swap does not call it off (its upload is the page it was answered
   *  for, kept alive hidden). The app may still give up until the reply. */
  saveAnswered?: boolean
  /** A share-view ask's popup, once open, and how to give that ask up. */
  popup?: Window
  popupStop?: AbortController
}

/**
 * The ask's popup looks gone. Never an answer (see `popupClosed`: a sign-in
 * passing through a COOP page reads the same while the person is still in
 * it) — only what lets a fresh ask take this one's place.
 */
function popupGone(owner: Owner): boolean {
  return !!owner.popup && popupClosed(owner.popup)
}

/** How asking the person ended: the server's outcome (and whether a modal
 *  picker is still showing — a save's, after its answer), or a reply for the
 *  app that ends the relay. */
type Asked =
  | { kind: 'settled'; settled: HandoffOutcome | null; dialogShowing: boolean }
  | { kind: 'failed'; reply: Record<string, unknown> }


export function useHandoffRelay({ baseUrl, accountsContext, shownKey }: {
  baseUrl: string | null | undefined
  /** `'share'` on a share-view host: the picker is a popup there. */
  accountsContext?: string
  /** Changes whenever the viewer swaps which frame is on screen — an ask from
   *  the frame that left the screen ends. */
  shownKey?: unknown
}): {
  handlers: MessageHandlerMap
  ui: ReactElement | null
  /** Hide the viewer's app frames (display: none) while set — an app fights
   *  the picker for focus. */
  framesHidden: boolean
} {
  const [active, setActive] = useState<Active | null>(null)
  // An app fights the picker for focus: the viewer hides its app frames until
  // the picker closes (HandoffDialog `onContested`). All of them — which frame
  // took focus cannot be seen from here (a steal leaves this page's
  // `activeElement` on the picker), and the reload buffer is a document too.
  const [framesHidden, setFramesHidden] = useState(false)
  const ownerRef = useRef<Owner | null>(null)
  const ownerSeqRef = useRef(0)
  // Every ask still running in this viewer — the current one, and any a newer
  // ask took the place of (a popup that looked gone) until it settles: the
  // app may still give up on it, and a swap still takes it off screen.
  const liveRef = useRef(new Set<Owner>())
  // What is waiting on the modal's next word — or that word, when the page
  // spoke between two waits (a save's "Hide" while its outcome is fetched).
  const waiterRef = useRef<((outcome: HandoffDialogOutcome) => void) | null>(null)
  const heardRef = useRef<HandoffDialogOutcome | null>(null)
  // How to give up a popup ask.
  const popupRef = useRef<AbortController | null>(null)
  // One drop redeemed at a time: each is a first-party call made as the person.
  const dropRef = useRef(false)
  const baseUrlRef = useRef(baseUrl)
  baseUrlRef.current = baseUrl
  const toast = useToastOptional()
  const { t } = useTranslation('files')
  // The person saw the loading dialog: when it goes because the picker could
  // not open, they are told — not left wondering what their click did.
  const openFailedRef = useRef(() => {})
  openFailedRef.current = () => toast?.error(t('handoff.openFailed'))

  // Start noting Papan's own input as early as a viewer exists.
  useEffect(() => { papanInput() }, [])

  // Focus goes back to the app only once the modal is gone from the page —
  // while its frame is live, the dialog takes focus straight back (it holds
  // focus against the app). A new picker instead drops the pending return.
  const focusOnCloseRef = useRef<(() => void) | null>(null)
  useEffect(() => {
    const pending = focusOnCloseRef.current
    focusOnCloseRef.current = null
    if (!active) pending?.()
  }, [active])
  const returnFocusOnClose = (relay: Relay) => { focusOnCloseRef.current = relay.returnFocus }

  /** The modal for `relay`'s ask goes away NOW — its request settles behind
   *  it. A closed picker must not stay over the page (the person cannot use
   *  it, and cannot close it again) while the host cancels and reads, nor
   *  forever if one of those stalls. */
  const closeModal = (relay: Relay, returnFocus = true) => {
    if (ownerRef.current !== relay.owner) return
    // Focus goes back only after a picker the person was in — not after the
    // loading dialog of an ask that never showed one.
    if (returnFocus) returnFocusOnClose(relay)
    setActive(null)
    setFramesHidden(false)
  }

  // The modal while its request still registers (no page yet): how to give
  // focus back if the person closes it then.
  const activeRef = useRef<Active | null>(null)
  useEffect(() => { activeRef.current = active }, [active])
  const loadingReturnRef = useRef<(() => void) | null>(null)

  /** The modal's word: to whatever waits on it, or kept for the next wait. */
  const hear = (outcome: HandoffDialogOutcome) => {
    const waiter = waiterRef.current
    waiterRef.current = null
    if (waiter) { waiter(outcome); return }
    heardRef.current = outcome
    // Said while the request still registers: the dialog goes NOW, and the
    // page's picker with it — never held until the registration answers
    // (a stalled one can take the request's whole timeout). The word stands
    // for when it does: the request is then settled as closed.
    if (activeRef.current?.pid === null) {
      const owner = ownerRef.current
      if (owner) releasePage(owner)
      focusOnCloseRef.current = loadingReturnRef.current
      setActive(null)
    }
  }

  // A different app (or none) in this viewer ends the ask it made.
  useEffect(() => () => {
    if (ownerRef.current) hear('dismissed')
    popupRef.current?.abort()
  }, [baseUrl])

  // The viewer reloaded the app into its other frame: an ask from the frame
  // that left the screen ends, whatever phase it is in — still beginning (the
  // dialog loading), in the modal or the popup — since its answer would reach a page
  // nobody sees. (A save the person already answered goes on: its upload is
  // that page's.)
  useEffect(() => {
    // An ask a newer one replaced only waits on its popup's settling: marked,
    // its answer (if the person gave one just then) is voided, not relayed.
    for (const other of liveRef.current) {
      if (other !== ownerRef.current && other.frame && !frameOnScreen(other.frame) && other.requestId !== null && !other.saveAnswered) other.offScreen = true
    }
    const owner = ownerRef.current
    if (!owner?.frame || frameOnScreen(owner.frame) || owner.requestId === null || owner.saveAnswered) return
    owner.offScreen = true
    // In the modal, or its dialog still loading (which then goes at once).
    if (waiterRef.current || activeRef.current?.pid === null) hear('dismissed')
    popupRef.current?.abort()
  }, [shownKey])

  /** Show the modal for `pid` (or keep it showing) until the page's next word. */
  const waitForPage = (next?: Active) => new Promise<HandoffDialogOutcome>((resolve) => {
    // A word already said (the dialog closed while it loaded) answers at once —
    // the page is not shown after all.
    const heard = heardRef.current
    heardRef.current = null
    if (heard) { resolve(heard); return }
    if (next) setActive(next)
    waiterRef.current = resolve
  })

  /** The picker (modal or popup) for `owner` is gone: another app may ask. */
  const releasePage = (owner: Owner) => {
    if (page.picker === owner) page.picker = null
  }

  /** `owner`'s ask is over: no picker, no slot — unless a newer ask already
   *  holds them. */
  const end = (owner: Owner) => {
    liveRef.current.delete(owner)
    releasePage(owner)
    if (ownerRef.current !== owner) return
    ownerRef.current = null
    waiterRef.current = null
    heardRef.current = null
    popupRef.current = null
    setActive(null)
    setFramesHidden(false)
  }


  const openRelay = relayOpener({
    baseUrlRef,
    ownerRef,
    nextOwnerId: () => ++ownerSeqRef.current,
    framed: () => window.top !== window.self,
  })

  /**
   * Ask the person — the picker at once, for an ask made with a click; none
   * for an app asking by itself. `beginModal` begins the request as the
   * person (Papan); `beginBody` is what a popup registers through the share
   * session. The popup opens synchronously inside the app's click — so
   * nothing may be awaited before this.
   */
  const ask = (relay: Relay, kind: Kind, beginModal: () => Promise<{ pid: string }>, beginBody: Record<string, unknown>, appActivated: unknown): Promise<Asked> => {
    // `gesture_required`, never `cancelled`: the person refused nothing here —
    // the app asked without a click, and its author is the one who must fix it.
    // An app told `cancelled` is told to stay quiet (the SDK says so), which is
    // exactly wrong for a mistake only a fresh click can clear. pd.app answers
    // the same for the same mistake, and keeps `popup_blocked` for its own.
    const unasked: Asked = { kind: 'failed', reply: { status: 'gesture_required' } }
    // The page's activation may be Papan's own: the click that just CLOSED a
    // picker activates this page for a few seconds. The app's frame says
    // whether the click is ITS (the SDK reads its own activation, which a click
    // in Papan's picker never gives it) — so an app asking again as it hears
    // this does not put the picker straight back, forever.
    if (appActivated === false || !clickedNow(relay.owner.frame)) return Promise.resolve(unasked)
    return show(relay, kind, beginModal, beginBody)
  }

  /** Show Papan's picker — a popup in share-view, the modal in Papan — unless
   *  another viewer's is up (one per page). */
  const show = (relay: Relay, kind: Kind, beginModal: () => Promise<{ pid: string }>, beginBody: Record<string, unknown>): Promise<Asked> => {
    const { owner } = relay
    // One picker per page — a popup that looks gone no longer holds the slot.
    if (page.picker && page.picker !== owner && !popupGone(page.picker)) return Promise.resolve({ kind: 'failed', reply: { status: 'busy' } })
    page.picker = owner
    if (accountsContext === 'share') {
      const controller = new AbortController()
      popupRef.current = controller
      owner.popupStop = controller
      return askByPopup({
        serve: relay.serve,
        begin: { kind, ...beginBody },
        signal: controller.signal,
        onOpen: (popup) => { owner.popup = popup },
      }).then((r): Asked => {
        releasePage(owner)
        if (r.kind === 'outcome') return { kind: 'settled', settled: r.outcome, dialogShowing: false }
        if (r.kind === 'refused') return { kind: 'failed', reply: { status: 'too_large', limit: r.refusal.limit, actual: r.refusal.actual } }
        return r.status === 'cancelled'
          ? { kind: 'settled', settled: { status: 'cancelled' }, dialogShowing: false }
          : { kind: 'failed', reply: { status: r.status } }
      })
    }
    return (async (): Promise<Asked> => {
      // #1316: the ask belongs to the session it began in — once that session ended, nothing more of it is sent
      // (the cancel and the outcome read would go out as the NEXT session); the app hears "unavailable"
      const start = currentSessionSignal()
      // The dialog at once, loading, while the request is registered: a click
      // that shows nothing for a second gets clicked again. A word the person
      // says meanwhile (closing it) is kept for when the page would show.
      const tenantId = getActiveTenantId() ?? ''
      if (ownerRef.current === owner) {
        loadingReturnRef.current = relay.returnFocus
        setActive({ kind, pid: null, tenantId })
      }
      let pid: string
      try {
        pid = (await beginModal()).pid
      } catch (err) {
        closeModal(relay, false)
        // Too large, or options Papan refuses (400), are the app's to explain —
        // trying again would not help; rate limits are toasted already.
        const status = (err as { status?: number }).status
        if (status !== 400 && status !== 413 && status !== 429) openFailedRef.current()
        // Over the ceiling: the app hears by how much. Anything else says no more.
        // (Several files: their total.)
        const size = Array.isArray(beginBody.size)
          ? beginBody.size.reduce((sum: number, n: unknown) => sum + (typeof n === 'number' ? n : 0), 0)
          : beginBody.size
        return (err as { status?: number }).status === 413 && typeof size === 'number'
          ? { kind: 'failed', reply: { status: 'too_large', limit: HANDOFF_LIMITS.maxBytes, actual: size } }
          : { kind: 'failed', reply: { status: 'unavailable' } }
      }
      // A viewer that moved on, an app that gave up, or an ask replaced or
      // ended meanwhile: the request is given up unshown.
      const shown = ownerRef.current === owner && baseUrlRef.current === relay.servedFrom && !owner.appGaveUp
        && !owner.offScreen && !!owner.frame && frameOnScreen(owner.frame)
      const outcome: HandoffDialogOutcome = shown ? await waitForPage({ kind, pid, tenantId }) : 'dismissed'
      // A save's modal stays after the answer (it follows the upload); every
      // other word is the modal going away now — and another app may ask.
      const dialogShowing = kind === 'save' && outcome === 'answered'
      if (dialogShowing) owner.saveAnswered = true
      else {
        releasePage(owner)
        // Its dialog goes — including one still loading for an ask that ended unshown.
        closeModal(relay, shown)
      }
      // Closed around the picker, or given up in it: the host makes sure the
      // request is settled before reading it (idempotent — first transition
      // wins, so an answer given in the same instant stands).
      if (sessionEndedSince(start)) return { kind: 'settled', settled: null, dialogShowing }
      if (outcome !== 'answered') await handoffApi.cancel(pid)
      const settled = await readOutcome(pid, start)
      return { kind: 'settled', settled, dialogShowing }
    })()
  }

  /** The app gave up waiting (its AbortSignal fired): close the picker it
   *  asked for. (After a save's answer the app stops by abandoning
   *  its upload, and the page shows the person that it stopped.) */
  const onAppCancel = (e: MessageEvent) => {
    const requestId = typeof e.data.requestId === 'string' ? e.data.requestId : null
    const owner = requestId === null ? undefined
      : [...liveRef.current].find((o) => o.requestId === requestId && o.frame?.contentWindow === e.source)
    if (!owner) return
    owner.appGaveUp = true
    if (owner !== ownerRef.current) {
      // An ask a newer one took the place of: only its popup wait is left.
      owner.popupStop?.abort()
      return
    }
    if (waiterRef.current) hear('dismissed')
    // Still registering (the dialog loading, no word to wait on yet): the
    // dialog goes now, not when the request's registration answers.
    else setActive(prev => (prev && prev.pid === null ? null : prev))
    popupRef.current?.abort()
  }

  /** An answer that can no longer reach the app that asked: it gave up, its
   *  frame was swapped off screen, or the viewer moved on. Its grant is
   *  voided (a save called off) rather than left live for no one. */
  const undeliverable = (relay: Relay) =>
    relay.owner.appGaveUp || relay.owner.offScreen || baseUrlRef.current !== relay.servedFrom

  const handlers: MessageHandlerMap = {
    'papan:files:pick': async (e) => {
      const relay = openRelay(e, 'papan:files:pick:response')
      if (!relay) return
      liveRef.current.add(relay.owner)
      const asked = await ask(relay, 'read', () => handoffApi.begin(relay.serve.sessionId, e.data.options), { options: e.data.options }, e.data.activated)
      end(relay.owner)
      if (asked.kind === 'failed') { relay.reply(asked.reply); return }
      const { settled } = asked
      if (settled?.status === 'answered' && settled.answer.kind === 'read') {
        const base = relay.grantUrl('read', settled.answer.grant)
        // Nothing will fetch the bytes: void the grant now.
        if (undeliverable(relay)) { voidGrant(base); return }
        relay.reply({
          status: 'answered',
          release: base,
          files: settled.answer.files.map((f, n) => ({ ...f, url: `${base}/${n}` })),
          ...(settled.answer.folders?.length ? { folders: settled.answer.folders } : {}),
        })
      } else if (settled?.status === 'cancelled') {
        relay.reply({ status: 'cancelled', ...(settled.refusal ? { refusal: settled.refusal } : {}) })
      } else {
        relay.reply({ status: 'unavailable' })
      }
    },
    'papan:files:pick:cancel': onAppCancel,

    'papan:files:save': async (e) => {
      const relay = openRelay(e, 'papan:files:save:response')
      if (!relay) return
      liveRef.current.add(relay.owner)
      const { owner } = relay
      const { options, size } = e.data
      const asked = await ask(relay, 'save', () => handoffApi.beginSave(relay.serve.sessionId, options, size), { options, size }, e.data.activated)
      if (asked.kind === 'failed') { end(owner); relay.reply(asked.reply); return }
      const { settled } = asked
      if (settled?.status !== 'answered' || settled.answer.kind !== 'save') {
        // The page said answered but the answer could not be read: its modal
        // goes away now.
        if (asked.dialogShowing) closeModal(relay)
        end(owner)
        relay.reply({ status: settled?.status === 'cancelled' ? 'cancelled' : 'unavailable' })
        return
      }
      // One upload URL per file (a save of several has one each).
      const uploadUrls = saveAnswerGrants(settled.answer).map((grant) => relay.grantUrl('save', grant))
      // No upload is coming: call the save off, so the person's picker says
      // so instead of waiting on it.
      if (undeliverable(relay)) for (const url of uploadUrls) voidGrant(url, { 'Tus-Resumable': '1.0.0' })
      else relay.reply({ status: 'answered', uploadUrl: uploadUrls[0], uploadUrls })
      // The modal follows the upload to its end and stays until the person
      // closes it (or it is closed around them — the upload goes on). A late
      // cancel from the app is no longer a cancel of the picker.
      if (asked.dialogShowing && ownerRef.current === owner) {
        owner.requestId = null
        await waitForPage()
        closeModal(relay)
      }
      end(owner)
    },
    'papan:files:save:cancel': onAppCancel,

    'papan:files:drop': async (e) => {
      const relay = openRelay(e, 'papan:files:drop:response', { exclusive: false })
      if (!relay) return
      if (dropRef.current) { relay.reply({ status: 'busy' }); return }
      dropRef.current = true
      const token = typeof e.data.token === 'string' ? e.data.token : ''
      const { options } = e.data
      const { sessionId, origin } = relay.serve
      const result: ShareDropResult | null = await (accountsContext === 'share'
        ? shareDrop(`${origin}/s/${encodeURIComponent(sessionId)}/_papan/handoff/drops`, { token, options })
        : handoffApi.drop(sessionId, token, options).catch(() => null)
      ).finally(() => { dropRef.current = false })
      if (result?.ok) {
        const base = relay.grantUrl('read', result.grant)
        // The viewer moved on meanwhile: nothing will fetch the bytes.
        if (baseUrlRef.current !== relay.servedFrom) { voidGrant(base); return }
        relay.reply({
          status: 'answered',
          release: base,
          files: result.files.map((f, n) => ({ ...f, url: `${base}/${n}` })),
          ...(result.folders?.length ? { folders: result.folders } : {}),
        })
      } else if (result?.reason === 'too_large' || result?.reason === 'too_many') {
        relay.reply({ status: 'cancelled', refusal: result })
      } else {
        relay.reply({ status: result?.reason === 'sign_in_required' ? 'sign_in_required' : 'unavailable' })
      }
    },
  }

  // Only for the ask this dialog belongs to: a steal counted as the dialog
  // closes must not hide the frames under the next ask.
  const dialogOwner = ownerRef.current
  const ui = active
    ? (
      <HandoffDialog
        pid={active.pid}
        tenantId={active.tenantId}
        kind={active.kind}
        onDone={hear}
        onContested={() => { if (dialogOwner && ownerRef.current === dialogOwner) setFramesHidden(true) }}
      />
    )
    : null
  return { handlers, ui, framesHidden }
}

type Relay = NonNullable<ReturnType<ReturnType<typeof relayOpener>>>

/**
 * The opening every relay shares: who asked, how to answer ONLY them, and
 * whether this viewer can show Papan's picker at all. Null when it has already
 * answered (unavailable, busy). An exclusive relay (a pick or save) takes the
 * slot as its `owner`: busy while this viewer's last ask is live, except a
 * popup that looks gone (closed, or passing through a sign-in), which gives way.
 */
function relayOpener({ baseUrlRef, ownerRef, nextOwnerId, framed }: {
  baseUrlRef: { current: string | null | undefined }
  ownerRef: { current: Owner | null }
  nextOwnerId: () => number
  framed: () => boolean
}) {
  /** `exclusive: false` — a relay that shows no picker (a drop) takes no slot. */
  return (e: MessageEvent, responseType: string, { exclusive = true }: { exclusive?: boolean } = {}) => {
    const src = e.source as Window | null
    const requestId = typeof e.data.requestId === 'string' ? e.data.requestId : null
    if (!src || !requestId) return null
    const servedFrom = baseUrlRef.current
    // #1408 R6: reply to the origin the request came from — on an app's own preview origin a frame
    // the app navigated elsewhere meanwhile gets nothing (an opaque frame's origin is 'null': '*').
    const replyTarget = replyTargetOf(e)
    const reply = (body: Record<string, unknown>) => {
      // The app's iframe window survives navigation — a reply must not reach
      // whatever app is showing by the time the person answers.
      if (baseUrlRef.current !== servedFrom) return
      try { src.postMessage({ type: responseType, requestId, ...body }, replyTarget) } catch { /* gone */ }
    }
    const serve = serveSessionOf(servedFrom)
    const frame = frameOf(src)
    // No serve session (inline preview), or this SPA itself framed (the
    // clickjack guard every host-drawn picker here keeps): no picker. Nor for
    // an app the person cannot see (a hidden deck, the viewer's reload
    // buffer, a background tab).
    if (!serve || framed() || (exclusive && (!frame || !frameIsShown(frame)))) {
      reply({ status: 'unavailable' })
      return null
    }
    let owner: Owner = { id: 0, frame, requestId, appGaveUp: false, offScreen: false }
    if (exclusive) {
      const current = ownerRef.current
      // A picker the person is in holds; a popup that looks gone (closed — or
      // passing through a sign-in) gives way.
      const gone = !!current && popupGone(current)
      if (current && !gone) {
        reply({ status: 'busy' })
        return null
      }
      owner = { ...owner, id: nextOwnerId() }
      ownerRef.current = owner
      // Its app hears `cancelled` (the popup's request is settled as closed —
      // or as the person answered it, if they did just then).
      if (current && gone) {
        current.popupStop?.abort()
        if (page.picker === current) page.picker = null
      }
    }
    const grantUrl = (kind: Kind, grant: string) =>
      `${serve.origin}/s/${encodeURIComponent(serve.sessionId)}/_papan/handoff/${kind}/${grant}`
    /**
     * Back to the app, once a picker it showed is gone — and only from where
     * the picker left it (nowhere, or still in the closing dialog): never
     * taken from something the person has moved on to, or an app could pull
     * their typing out of Papan's own fields.
     */
    const returnFocus = () => {
      // The same app only: the viewer reuses its frame for the next one.
      if (baseUrlRef.current !== servedFrom) return
      const current = document.activeElement
      const leftByPicker = !current || current === document.body || !!current.closest(`[${HANDOFF_DIALOG_ATTR}]`)
      if (leftByPicker && frame?.isConnected && frameIsShown(frame)) frame.focus()
    }
    return { requestId, reply, serve, servedFrom, grantUrl, returnFocus, owner }
  }
}

/** The request's outcome, asked again for a while: after the person
 *  answered, a network blip must not drop their answer (the app would hear
 *  "unavailable" while the person's save waits for an upload that never
 *  comes). About 20 s in all — well inside the request's life. */
async function readOutcome(pid: string, start: AbortSignal | undefined): Promise<HandoffOutcome | null> {
  // #1316: asked (and asked again) only within the session the ask began in (`start`) — a read after it ended would go
  // out as the NEXT session (the app hears "unavailable", as for an answer that could not be read)
  for (let attempt = 1; ; attempt++) {
    if (sessionEndedSince(start)) return null
    let ended = false
    const settled = await handoffApi.outcome(pid).catch((err: unknown) => { ended = sessionEndedSince(start, err); return null })
    if (settled || ended || attempt >= READ_OUTCOME_TRIES) return settled
    await new Promise((r) => setTimeout(r, Math.min(400 * attempt, 3_000)))
  }
}
const READ_OUTCOME_TRIES = 10

/** Redeem a drop through the share session — bounded like the first-party
 *  call (`request`'s timeout): a stalled response must not hold the viewer's
 *  one drop slot, answering every later drop `busy`. */
async function shareDrop(url: string, body: { token: string; options: unknown }): Promise<ShareDropResult | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), DROP_TIMEOUT_MS)
  try {
    const res = await sessionFetch(url, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    })
    return res.ok ? await res.json() as ShareDropResult : null
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}
const DROP_TIMEOUT_MS = 30_000
/** The share session's drop route also says the visitor must sign in. */
type ShareDropResult = HandoffDropResult | { ok: false; reason: 'sign_in_required' }

/** Void a grant the app will never use (a read's, or a save's upload URL):
 *  its own URL, credential-less — only the app session's holder can name it. */
function voidGrant(url: string, headers: Record<string, string> = {}): void {
  void fetch(url, { method: 'DELETE', credentials: 'omit', keepalive: true, headers }).catch(() => {})
}

/** The iframe element whose document is `src`, if it is one of this page's. */
function frameOf(src: Window): HTMLIFrameElement | null {
  return Array.from(document.getElementsByTagName('iframe')).find((f) => f.contentWindow === src) ?? null
}

/**
 * Whether the person can see this app: this tab in front, and the frame on
 * screen (`frameOnScreen`).
 */
function frameIsShown(frame: HTMLIFrameElement): boolean {
  return document.visibilityState !== 'hidden' && frameOnScreen(frame)
}

/**
 * Whether the frame is laid out and shown in the page — a keep-alive deck is
 * `display: none` (no box), the viewer's reload buffer `visibility: hidden`
 * (a stale document still able to post), a background app window fully
 * transparent (`opacity: 0` on the window — it stays laid out, over the
 * shell). Whatever the tab: a person who switches away mid-ask comes back
 * to it.
 */
function frameOnScreen(frame: HTMLIFrameElement): boolean {
  if (frame.getClientRects().length === 0 || getComputedStyle(frame).visibility === 'hidden') return false
  for (let el: Element | null = frame; el; el = el.parentElement) {
    if (getComputedStyle(el).opacity === '0') return false
  }
  return true
}

/**
 * Whether this ask comes with a click — the page is activated (a press in the
 * app's frame activates its parents) — and not one made in ANOTHER frame that
 * is not Papan's (another app, an office editor: a press there usually leaves
 * focus there, and never reaches this document). Which of the page's frames
 * the press landed in cannot be told apart beyond that: the picker is the
 * person's to answer or close.
 * An engine without the activation API cannot say: the ask goes on.
 */
function clickedNow(frame: HTMLIFrameElement | null): boolean {
  const activation = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation
  if (activation && !activation.isActive) return false
  const input = papanInput()
  const focused = document.activeElement
  return !(focused instanceof HTMLIFrameElement && focused !== frame && !input?.isOwnFrame(focused))
}
